import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
} from "@headlessui/react";
import {
  MagnifyingGlassIcon,
  XMarkIcon,
  ArrowPathIcon,
  CheckIcon,
} from "@heroicons/react/24/outline";
import { Company } from "../../api/fetchMyCompanies/types";
import { fetchMyCompanies } from "../../api/fetchMyCompanies/fetchMyCompaniesApi";
import InfiniteScroll from "react-infinite-scroll-component";
import { useDebouncedValue } from "../../hooks/useDebouncedValue";
import { useLocation } from "react-router-dom";
import { getSectionFromPath } from "@/utils/navigationHelpers";
import { cn } from "@/utils/utils";

interface CompanySwitcherModalProps {
  open: boolean;
  onClose: () => void;
}

const CompanySwitcherModal: React.FC<CompanySwitcherModalProps> = ({
  open,
  onClose,
}) => {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const [page, setPage] = useState<number>(1);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [search, setSearch] = useState<string>("");
  const [isInitialLoading, setIsInitialLoading] = useState<boolean>(false);
  const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false);
  const debouncedSearch = useDebouncedValue(search, 500);

  const location = useLocation();

  useEffect(() => {
    if (open) {
      setCompanies([]);
      setPage(1);
      setHasMore(true);
      setIsInitialLoading(true);
      loadCompanies(1, debouncedSearch, true);
    }
  }, [debouncedSearch, open]);

  useEffect(() => {
    if (!open) {
      setCompanies([]);
      setPage(1);
      setHasMore(true);
      setSelectedCompany(null);
      setSearch("");
      setIsInitialLoading(false);
      setIsLoadingMore(false);
    }
  }, [open]);

  const loadCompanies = (
    pageNumber: number,
    searchQuery: string,
    isInitial: boolean = false,
  ) => {
    if (!isInitial) {
      setIsLoadingMore(true);
    }

    fetchMyCompanies(pageNumber, searchQuery)
      .then((response) => {
        const newCompanies = response.data;

        setCompanies((prevCompanies) =>
          pageNumber === 1 ? newCompanies : [...prevCompanies, ...newCompanies],
        );

        if (newCompanies.length < 10) {
          setHasMore(false);
        }
        setIsInitialLoading(false);
        setIsLoadingMore(false);
      })
      .catch((error) => {
        console.error("Error fetching companies:", error);
        setHasMore(false);
        setIsInitialLoading(false);
        setIsLoadingMore(false);
      });
  };

  const fetchNextCompanies = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    loadCompanies(nextPage, debouncedSearch, false);
  };

  const handleCompanySelect = (company: Company) => {
    setSelectedCompany(company);
  };

  const handleConfirmSelection = () => {
    if (selectedCompany) {
      localStorage.setItem("selectedCompanyUuid", selectedCompany.companyUuid);

      const sectionInfo = getSectionFromPath(location.pathname);
      if (sectionInfo) {
        window.location.href = sectionInfo.defaultRoute;
      } else {
        window.location.href = "/";
      }

      onClose();
    }
  };

  const renderContent = () => {
    if (isInitialLoading) {
      return (
        <div className="flex h-[280px] items-center justify-center">
          <div className="flex flex-col items-center">
            <ArrowPathIcon className="h-4 w-4 animate-spin text-gray-400" />
            <span className="mt-2 text-[13px] text-gray-500 dark:text-gray-400">
              Loading...
            </span>
          </div>
        </div>
      );
    }

    return (
      <InfiniteScroll
        dataLength={companies.length}
        endMessage={
          companies.length > 0 ? (
            <div className="py-2 text-center text-[12px] text-gray-400">
              End of list
            </div>
          ) : (
            <div className="py-6 text-center">
              <p className="text-[13px] text-gray-500 dark:text-gray-400">
                No companies found
              </p>
            </div>
          )
        }
        hasMore={hasMore && !isLoadingMore}
        height={280}
        loader={
          isLoadingMore ? (
            <div className="flex items-center justify-center py-2">
              <ArrowPathIcon className="h-3.5 w-3.5 animate-spin text-gray-400" />
            </div>
          ) : null
        }
        next={fetchNextCompanies}
      >
        <div>
          {companies.map((company) => (
            <button
              className={cn(
                "w-full px-3 py-2 text-left transition-colors",
                selectedCompany?.companyUuid === company.companyUuid
                  ? "bg-gray-100 dark:bg-gray-800"
                  : "hover:bg-gray-50 dark:hover:bg-gray-800/50",
              )}
              key={company.companyUuid}
              onClick={() => handleCompanySelect(company)}
            >
              <div className="flex items-center justify-between">
                <div className="min-w-0 flex-1">
                  <div className="text-[13px] font-medium text-gray-900 dark:text-white truncate">
                    {company.companyName}
                  </div>
                  {company.intacctId && (
                    <div className="text-[12px] text-gray-500 dark:text-gray-400">
                      ID: {company.intacctId}
                    </div>
                  )}
                </div>
                {selectedCompany?.companyUuid === company.companyUuid && (
                  <CheckIcon className="h-3.5 w-3.5 text-primary flex-shrink-0 ml-2" />
                )}
              </div>
            </button>
          ))}
        </div>
      </InfiniteScroll>
    );
  };

  return (
    <Dialog className="relative z-50" onClose={onClose} open={open}>
      <DialogBackdrop className="fixed inset-0 bg-black/20 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-200 data-[leave]:duration-150" />

      <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-4">
          <DialogPanel className="relative w-full max-w-sm transform overflow-hidden bg-white dark:bg-gray-900 shadow-lg transition-all data-[closed]:scale-95 data-[closed]:opacity-0 data-[enter]:duration-200 data-[leave]:duration-150 border border-gray-200 dark:border-gray-700">
            {/* Header */}
            <div className="flex items-center justify-between px-3 py-2.5 border-b border-gray-200 dark:border-gray-700">
              <DialogTitle className="text-sm font-medium text-gray-900 dark:text-white">
                Switch Company
              </DialogTitle>
              <button
                aria-label="Close"
                className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                onClick={onClose}
              >
                <XMarkIcon className="h-3.5 w-3.5" />
              </button>
            </div>

            {/* Search */}
            <div className="px-3 py-2.5 border-b border-gray-200 dark:border-gray-700">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                <input
                  className="w-full border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 dark:text-white pl-7 pr-3 py-1.5 text-[13px] placeholder-muted-foreground focus:border-gray-300 dark:focus:border-gray-600 focus:outline-none transition-colors"
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search companies..."
                  type="text"
                  value={search}
                />
              </div>
            </div>

            {/* Company List */}
            <div>{renderContent()}</div>

            {/* Footer */}
            <div className="flex gap-2 px-3 py-2.5 border-t border-gray-200 dark:border-gray-700">
              <button
                className="flex-1 px-2.5 py-1.5 text-[13px] font-medium text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                onClick={onClose}
                type="button"
              >
                Cancel
              </button>
              <button
                className={cn(
                  "flex-1 px-2.5 py-1.5 text-[13px] font-medium text-white transition-colors",
                  selectedCompany
                    ? "bg-primary hover:bg-primary/90"
                    : "bg-muted-foreground/50 cursor-not-allowed",
                )}
                disabled={!selectedCompany}
                onClick={handleConfirmSelection}
                type="button"
              >
                {selectedCompany ? "Switch" : "Select a company"}
              </button>
            </div>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
};

export default CompanySwitcherModal;
